import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { UserPlus, Shield, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useListBoardAccess, useAddBoardAccess, useRemoveBoardAccess } from "@/hooks/useBoard"
import { useAuth } from "@/hooks/useAuth"
import { useListMembers, useListRoles } from "@/hooks/useTeam"
import { useToast } from "@/hooks/useToast"
import { ROUTES } from "@/routes/path"

function getInitials(firstName: string, lastName: string, email: string) {
  if (firstName || lastName) {
    return [firstName, lastName]
      .filter(Boolean)
      .map((s) => s.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }
  if (email) return email.slice(0, 2).toUpperCase()
  return "?"
}

const INVITE_TEAM_VALUE = "__invite_team__"

interface ShareBoardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  boardId: string
}

export function ShareBoardDialog({ open, onOpenChange, boardId }: ShareBoardDialogProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { user } = useAuth()
  const currentAccountId = user?.id ?? null
  const { data: boardAccess } = useListBoardAccess(open ? boardId : null)
  const boardMembers = boardAccess?.members ?? []
  const boardRoleGrants = boardAccess?.role_grants ?? []
  const { data: orgMembers } = useListMembers()
  const { data: roles } = useListRoles()
  const addAccess = useAddBoardAccess()
  const removeAccess = useRemoveBoardAccess()

  const availableMembers =
    orgMembers?.filter((mem) => mem.id !== currentAccountId && !boardMembers.some((bm) => bm.account_id === mem.id)) ??
    []

  const [shareMemberValue, setShareMemberValue] = useState("")
  const [shareRoleValue, setShareRoleValue] = useState("")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 overflow-hidden rounded-xl border-0 p-0 shadow-xl dark:border dark:border-gray-800">
        <DialogHeader className="px-6 pb-4 pt-6">
          <DialogTitle className="text-lg font-semibold">Share board</DialogTitle>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Manage who can view this board.</p>
        </DialogHeader>
        <div className="space-y-5 px-6 pb-6">
          {/* Members section */}
          <section className="rounded-xl border border-gray-100 bg-zinc-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/40">
            <div className="mb-1 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm dark:border-gray-600/60 dark:bg-gray-700/60">
                <UserPlus className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">People</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Invite team members by name</p>
              </div>
            </div>
            <ul className="mt-3 max-h-28 space-y-1 overflow-y-auto">
              {boardMembers.length === 0 && (
                <li className="rounded-lg border border-gray-100 bg-white px-3 py-2.5 text-xs text-gray-400 dark:border-gray-600/40 dark:bg-gray-700/30 dark:text-gray-500">
                  No members yet
                </li>
              )}
              {boardMembers.map((m) => (
                <li
                  key={m.account_id}
                  className="group flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2 transition-colors hover:border-gray-200 dark:border-gray-600/40 dark:bg-gray-700/30 dark:hover:border-gray-600"
                >
                  <Avatar className="h-8 w-8 shrink-0 border border-gray-100 dark:border-gray-600">
                    <AvatarFallback className="bg-gray-100 text-xs font-medium text-gray-600 dark:bg-gray-600 dark:text-gray-300">
                      {getInitials(m.first_name, m.last_name, m.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                      {m.first_name || m.last_name ? `${m.first_name} ${m.last_name}`.trim() : "—"}
                    </p>
                    {m.email && <p className="truncate text-xs text-gray-500 dark:text-gray-400">{m.email}</p>}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                    onClick={() => {
                      removeAccess.mutate(
                        { boardId, payload: { account_id: m.account_id } },
                        {
                          onSuccess: () => toast({ description: "Member removed" }),
                          onError: (e: any) =>
                            toast({
                              description: e?.response?.data?.error || "Failed to remove member",
                              variant: "destructive",
                            }),
                        }
                      )
                    }}
                    disabled={removeAccess.isPending}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
            <Select
              value={shareMemberValue}
              onValueChange={(value) => {
                if (!value) return
                setShareMemberValue("")
                if (value === INVITE_TEAM_VALUE) {
                  onOpenChange(false)
                  navigate(ROUTES.DASHBOARD.SETTINGS.TEAM)
                  return
                }
                addAccess.mutate(
                  { boardId, payload: { account_id: value } },
                  {
                    onSuccess: () => toast({ description: "Member added" }),
                    onError: (e: any) =>
                      toast({
                        description: e?.response?.data?.error || "Failed to add member",
                        variant: "destructive",
                      }),
                  }
                )
              }}
            >
              <SelectTrigger className="mt-3 h-9 rounded-lg border-gray-100 bg-white text-xs font-normal text-gray-500 dark:border-gray-600 dark:bg-gray-700/30 dark:text-gray-400">
                <SelectValue placeholder="Add a person..." />
              </SelectTrigger>
              <SelectContent>
                {availableMembers.length === 0 ? (
                  <SelectItem value={INVITE_TEAM_VALUE} className="text-sm text-primary">
                    <span className="flex items-center gap-2">
                      <UserPlus className="h-3.5 w-3.5" />
                      Invite team member
                    </span>
                  </SelectItem>
                ) : (
                  availableMembers.map((mem) => (
                    <SelectItem key={mem.id} value={mem.id} className="text-sm">
                      {mem.first_name || mem.last_name ? `${mem.first_name} ${mem.last_name}`.trim() : mem.email}
                      {mem.email && (mem.first_name || mem.last_name) ? ` · ${mem.email}` : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </section>

          {/* Roles section */}
          <section className="rounded-xl border border-gray-100 bg-zinc-50/50 p-4 dark:border-gray-700/60 dark:bg-gray-800/40">
            <div className="mb-1 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm dark:border-gray-600/60 dark:bg-gray-700/60">
                <Shield className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Roles</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">All members with this role get access</p>
              </div>
            </div>
            <ul className="mt-3 max-h-28 space-y-1.5 overflow-y-auto">
              {boardRoleGrants.length === 0 && (
                <li className="rounded-lg border border-gray-100 bg-white px-3 py-2.5 text-xs text-gray-400 dark:border-gray-600/40 dark:bg-gray-700/30 dark:text-gray-500">
                  No roles yet
                </li>
              )}
              {boardRoleGrants.map((g) => (
                <li
                  key={g.role_id}
                  className="group flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-white px-3 py-2 transition-colors hover:border-gray-200 dark:border-gray-600/40 dark:bg-gray-700/30 dark:hover:border-gray-600"
                >
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{g.role_name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                    onClick={() => {
                      removeAccess.mutate(
                        { boardId, payload: { role_id: g.role_id } },
                        {
                          onSuccess: () => toast({ description: "Role access removed" }),
                          onError: (e: any) =>
                            toast({
                              description: e?.response?.data?.error || "Failed to remove role",
                              variant: "destructive",
                            }),
                        }
                      )
                    }}
                    disabled={removeAccess.isPending}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
            <Select
              value={shareRoleValue}
              onValueChange={(value) => {
                if (!value) return
                setShareRoleValue("")
                addAccess.mutate(
                  { boardId, payload: { role_id: value } },
                  {
                    onSuccess: () => toast({ description: "Role access added" }),
                    onError: (e: any) =>
                      toast({
                        description: e?.response?.data?.error || "Failed to add role",
                        variant: "destructive",
                      }),
                  }
                )
              }}
            >
              <SelectTrigger className="mt-3 h-9 rounded-lg border-gray-100 bg-white text-xs font-normal text-gray-500 dark:border-gray-600 dark:bg-gray-700/30 dark:text-gray-400">
                <SelectValue placeholder="Add a role..." />
              </SelectTrigger>
              <SelectContent>
                {roles
                  ?.filter((r) => !boardRoleGrants.some((bg) => bg.role_id === r.id))
                  .map((r) => (
                    <SelectItem key={r.id} value={r.id} className="text-sm">
                      {r.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </section>
        </div>
        <DialogFooter className="border-t border-gray-100 bg-zinc-50/50 px-6 py-4 dark:border-gray-800 dark:bg-gray-800/30">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
